import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, HostListener } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  NotificationService, QuestionService, AmplitudeService,
  QuestionTemplateService, QuestionTemplate
} from '@core/services';
import { ConfirmDialogComponent, MainWrapperComponent } from '@core/component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faEdit, faTrash, faPlus, faSave, faTimes, faGripVertical, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { CdkDragDrop, moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import { forkJoin } from 'rxjs';

interface Question {
  id?: number;
  question_text: string;
  status?: boolean;
  order?: number;
  template_id?: string;
}

const DEFAULT_QUESTIONS = [
  "What are the top challenges you are trying to address?",
  "What is the business impact of these challenges you mentioned earlier?",
  "What are the timelines for finalizing the solution?",
  "What happens if you don't finalize anything by the timeline you mentioned?",
  "Who will technically approve?",
  "Who will commercially approve?",
  "How do you plan to fund this initiative?",
  "Who else are you evaluating along with us?"
];

@Component({
  selector: 'app-add-question',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, ConfirmDialogComponent, MainWrapperComponent, FontAwesomeModule, DragDropModule],
  templateUrl: './add-question.component.html',
  styleUrl: './add-question.component.scss',
})
export class AddQuestionComponent implements OnInit {
  @ViewChild('confirmModal') confirmModal!: ConfirmDialogComponent;
  @ViewChild('deleteQuestionModal') deleteQuestionModal!: ConfirmDialogComponent;
  @ViewChild('deleteTemplateModal') deleteTemplateModal!: ConfirmDialogComponent;

  questionForm: FormGroup;
  isRequestPending = false;
  isEditMode = false;
  isPublished = false;
  email = '';
  savedQuestions: Question[] = [];
  questionsToDelete: number[] = [];
  deleteQuestionMessage = '';
  questionToDeleteIndex = -1;

  // ── Template state ────────────────────────────────────────────────────────
  templates: QuestionTemplate[] = [];
  currentTemplateId: string | null = null;
  templatesEnabled = true;  // always show — degrades gracefully if table missing
  showNewTemplateModal = false;
  newTemplateName = '';
  templateToDeleteId: string | null = null;

  // ── Rename state ─────────────────────────────────────────────────────────
  renamingTemplateId: string | null = null;
  renameValue = '';

  // ── Tab drag state ────────────────────────────────────────────────────────
  dragSourceIndex: number | null = null;
  dragOverIndex: number | null = null;

  // ── AI generator state ──────────────────────────────────────────────────
  isGenerating = false;
  aiPrompt = '';

  // ── FontAwesome icons ────────────────────────────────────────────────────
  faEdit = faEdit;
  faTrash = faTrash;
  faPlus = faPlus;
  faSave = faSave;
  faTimes = faTimes;
  faGripVertical = faGripVertical;
  faWandMagicSparkles = faWandMagicSparkles;

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _questionService: QuestionService,
    private readonly _templateService: QuestionTemplateService,
    private readonly _notificationService: NotificationService,
    private readonly _amplitudeService: AmplitudeService
  ) {
    this.questionForm = this._fb.group({
      questions: this._fb.array([])
    });
  }

  ngOnInit(): void {
    const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
    this.email = userInfo?.user?.email ?? '-';
    this._loadTemplates();
  }

  get questions(): FormArray {
    return this.questionForm.get('questions') as FormArray;
  }

  get pageTitle(): string {
    return 'Questions';
  }

  // ── Template tab methods ──────────────────────────────────────────────────

  switchTemplate(id: string): void {
    if (id === this.currentTemplateId) return;
    this.currentTemplateId = id;
    this.isEditMode = false;
    this.isPublished = false;
    this.questionsToDelete = [];
    this.questions.clear();
    this.savedQuestions = [];
    this._loadQuestions();
  }

  openNewTemplateModal(): void {
    this.newTemplateName = '';
    this.showNewTemplateModal = true;
  }

  closeNewTemplateModal(): void {
    this.showNewTemplateModal = false;
    this.newTemplateName = '';
  }

  createTemplate(): void {
    const name = this.newTemplateName.trim();
    if (!name) return;

    this._templateService.createTemplate(name).subscribe({
      next: (result) => {
        const newTemplate: QuestionTemplate = Array.isArray(result) ? result[0] : result;
        if (newTemplate?.id) {
          this.templates = [...this.templates, newTemplate];
          this._switchToNewTemplate(newTemplate.id);
        }
        this.closeNewTemplateModal();
      },
      error: () => {
        this._notify('Failed to create template');
        this.closeNewTemplateModal();
      }
    });
  }

  confirmDeleteTemplate(id: string, event: Event): void {
    event.stopPropagation();
    if (this.templates.length <= 1) {
      this._notify('You need at least one template');
      return;
    }
    this.templateToDeleteId = id;
    this.deleteTemplateModal.open();
  }

  handleDeleteTemplate(confirmed: boolean): void {
    if (!confirmed || !this.templateToDeleteId) {
      this.templateToDeleteId = null;
      return;
    }
    const id = this.templateToDeleteId;
    this.templateToDeleteId = null;

    this._templateService.deleteTemplate(id).subscribe({
      next: () => {
        this.templates = this.templates.filter(t => t.id !== id);
        if (this.currentTemplateId === id) {
          const next = this.templates[0];
          this.currentTemplateId = next?.id ?? null;
          this.isEditMode = false;
          this.savedQuestions = [];
          this.questions.clear();
          if (this.currentTemplateId) {
            this._loadQuestions();
          }
        }
        this._notify('Template deleted');
      },
      error: () => this._notify('Failed to delete template')
    });
  }

  // ── AI generator methods ─────────────────────────────────────────────────

  setAiPrompt(prompt: string): void {
    this.aiPrompt = prompt;
  }

  generateQuestions(): void {
    const prompt = this.aiPrompt.trim();
    if (!prompt || this.isGenerating) return;

    if (!this.isEditMode) {
      this.isEditMode = true;
      this.isPublished = false;
      this._populateFormForEditing();
    }

    this.isGenerating = true;
    this._templateService.generateQuestions(prompt).subscribe({
      next: (response) => {
        this.isGenerating = false;
        this.aiPrompt = '';
        this._loadGeneratedQuestions(response.questions);
      },
      error: () => {
        this.isGenerating = false;
        this._notify('Failed to generate questions. Please try again.');
      }
    });
  }


  addMore(index?: number): void {
    this._setRequestPending(false);
    this._amplitudeService.trackEvent('question_screen_add_more', { number_added: '1' });
    
    const newQuestionGroup = this._createQuestionFormGroup();
    
    if (index !== undefined) {
      // Insert after the current question
      this.questions.insert(index + 1, newQuestionGroup);
    } else {
      // Add to the end (for bottom button)
      this.questions.push(newQuestionGroup);
    }
  }

  startCustomQuestions(event?: Event): void {
    if (event) {
      event.stopPropagation();
      if (event instanceof KeyboardEvent) {
        event.preventDefault();
      }
    }

    if (!this.isEditMode) {
      this.toggleEditMode();
    }
  }

  loadDefaultQuestionSet(event?: Event): void {
    if (event) {
      event.stopPropagation();
      if (event instanceof KeyboardEvent) {
        event.preventDefault();
      }
    }

    this._queueExistingQuestionsForDeletion();
    this._loadDefaultQuestions({ autoEditMode: true });
    this.questionForm.markAsDirty();
  }

  toggleEditMode(): void {
    if (this.isEditMode) {
      // Cancel edit mode - reload saved questions
      this.cancelEdit();
    } else {
      // Enter edit mode
      this.isEditMode = true;
      this.isPublished = false;
      this._populateFormForEditing();
      if (this.questions.length === 0) {
        this.addMore();
      }
    }
  }

  cancelEdit(): void {
    this.isEditMode = false;
    this.isPublished = this.savedQuestions.length > 0;
    this.questionsToDelete = []; // Clear deletion queue
    this.questionToDeleteIndex = -1; // Reset delete index
    this.questions.clear();
    this._populateFormWithQuestions(this.savedQuestions);
  }

  removeQuestion(index: number): void {
    const questionControl = this.questions.at(index);
    const questionText = questionControl.get('question_text')?.value || 'Untitled question';
    const questionId = questionControl.get('id')?.value;
    
    // Store the index for the confirmation handler
    this.questionToDeleteIndex = index;
    
    // Set appropriate message based on whether question is saved or not
    if (questionId) {
      this.deleteQuestionMessage = `This will permanently remove the question from your meetings:\n\n"${questionText}"`;
    } else {
      this.deleteQuestionMessage = `Remove this question?\n\n"${questionText}"`;
    }
    
    // Always show confirmation dialog for better UX
    this.deleteQuestionModal.open();
  }

  handleDeleteQuestion(confirmed: boolean): void {
    if (!confirmed || this.questionToDeleteIndex === -1) {
      this.questionToDeleteIndex = -1;
      return;
    }

    const index = this.questionToDeleteIndex;
    const questionControl = this.questions.at(index);
    const questionId = questionControl.get('id')?.value;
    
    // If the question has an ID (exists in database), mark it for deletion
    if (questionId) {
      this.questionsToDelete.push(questionId);
    }
    
    // Remove from form array
    this.questions.removeAt(index);
    
    // Reset the index
    this.questionToDeleteIndex = -1;
  }

  get hasPendingChanges(): boolean {
    return this.questionsToDelete.length > 0 || this.questionForm.dirty;
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.hasPendingChanges) {
      $event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
    }
  }

  resetQuestions(): void {
    this.confirmModal.open();
  }

  handleResetQuestions(confirmed: boolean): void {
    if (!confirmed) return;

    this._trackEvent('question_screen_reset', { submit_date: new Date().toISOString() });
    this._setRequestPending(true);

    // First disable existing questions
    const existingQuestions = this.savedQuestions.filter(q => q.id);
    if (existingQuestions.length) {
      this._questionService.addOldQustion(existingQuestions.map(q => ({ ...q, status: false })))
        .subscribe({
          next: () => this._saveDefaultQuestions(),
          error: () => this._saveDefaultQuestions()
        });
    } else {
      this._saveDefaultQuestions();
    }
  }

  submit(): void {
    if (!this.questionForm.valid) {
      this._notify('Please fill in all required fields');
      return;
    }

    this._setRequestPending(true);
    const { newQuestions, oldQuestions } = this._splitQuestions();

    this._trackEvent('question_screen_submit', { number_of_ques: this.questions.length.toString() });

    // Handle deletions first, then updates/additions
    if (this.questionsToDelete.length > 0) {
      this._questionService.deleteQuestions(this.questionsToDelete).subscribe({
        next: () => {
          this._handleUpdatesAndAdditions(oldQuestions, newQuestions);
        },
        error: (error) => {
          this._deleteQuestionsIndividually(oldQuestions, newQuestions);
        }
      });
    } else {
      this._handleUpdatesAndAdditions(oldQuestions, newQuestions);
    }
  }

  private _handleUpdatesAndAdditions(oldQuestions: Question[], newQuestions: Question[]): void {
    // Get questions in the correct order from the form array
    const allQuestions = this.questionForm.value.questions as Question[];
    
    // Assign order values based on current form positions
    allQuestions.forEach((question, index) => {
      question.order = index + 1;
    });
    
    // Split after assigning order
    const orderedOldQuestions = allQuestions.filter(q => !!q.id);
    const orderedNewQuestions = allQuestions.filter(q => !q.id);
    
    // Handle API calls sequentially to avoid duplication
    if (orderedOldQuestions.length && orderedNewQuestions.length) {
      // Both old and new questions exist - call them sequentially
      this._questionService.addOldQustion(orderedOldQuestions).subscribe({
        next: () => {
          this._questionService.addNewQustion(orderedNewQuestions).subscribe({
            next: () => this._handleSubmitSuccess(),
            error: () => this._handleError('Error saving new questions')
          });
        },
        error: () => this._handleError('Error updating existing questions')
      });
    } else if (orderedOldQuestions.length) {
      // Only old questions
      this._questionService.addOldQustion(orderedOldQuestions).subscribe({
        next: () => this._handleSubmitSuccess(),
        error: () => this._handleError('Error updating questions')
      });
    } else if (orderedNewQuestions.length) {
      // Only new questions
      this._questionService.addNewQustion(orderedNewQuestions).subscribe({
        next: () => this._handleSubmitSuccess(),
        error: () => this._handleError('Error saving questions')
      });
    } else if (this.questionsToDelete.length > 0) {
      // Only deletions (handled above, just finish)
      this._handleSubmitSuccess();
    } else {
      this._handleNoChanges();
    }
  }

  private _deleteQuestionsIndividually(oldQuestions: Question[], newQuestions: Question[]): void {
    if (this.questionsToDelete.length === 0) {
      this._handleUpdatesAndAdditions(oldQuestions, newQuestions);
      return;
    }
    
    const deleteObservables = this.questionsToDelete.map(questionId => 
      this._questionService.deleteQuestionIndividually(questionId)
    );
    
    forkJoin(deleteObservables).subscribe({
      next: () => {
        this._handleUpdatesAndAdditions(oldQuestions, newQuestions);
      },
      error: (error) => {
        this._handleError('Error deleting some questions');
      }
    });
  }

  // ── Rename methods ────────────────────────────────────────────────────────

  startRename(t: QuestionTemplate, event: Event): void {
    event.stopPropagation();
    this.renamingTemplateId = t.id;
    this.renameValue = t.name;
  }

  saveRename(): void {
    const id = this.renamingTemplateId;
    const name = this.renameValue.trim();
    this.renamingTemplateId = null;
    this.renameValue = '';
    if (!id || !name) return;
    const prev = this.templates.find(t => t.id === id);
    if (prev?.name === name) return;
    // Optimistic update
    this.templates = this.templates.map(t => t.id === id ? { ...t, name } : t);
    this._templateService.renameTemplate(id, name).subscribe({
      error: () => {
        // Revert on failure
        if (prev) this.templates = this.templates.map(t => t.id === id ? prev! : t);
        this._notify('Failed to rename template');
      }
    });
  }

  cancelRename(): void {
    this.renamingTemplateId = null;
    this.renameValue = '';
  }

  onRenameKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') { event.preventDefault(); this.saveRename(); }
    if (event.key === 'Escape') { this.cancelRename(); }
  }

  // ── Tab drag methods ──────────────────────────────────────────────────────

  onTabDragStart(index: number): void {
    this.dragSourceIndex = index;
  }

  onTabDragOver(index: number, event: DragEvent): void {
    event.preventDefault();
    this.dragOverIndex = index;
  }

  onTabDrop(index: number, event: DragEvent): void {
    event.preventDefault();
    const src = this.dragSourceIndex;
    this.dragSourceIndex = null;
    this.dragOverIndex = null;
    if (src === null || src === index) return;
    const tabs = [...this.templates];
    const [moved] = tabs.splice(src, 1);
    tabs.splice(index, 0, moved);
    this.templates = tabs;
  }

  onTabDragEnd(): void {
    this.dragSourceIndex = null;
    this.dragOverIndex = null;
  }

  private _loadTemplates(): void {
    this._templateService.getTemplates().subscribe({
      next: (templates) => {
        if (!templates || templates.length === 0) {
          // No templates yet — show the empty state with the Add button visible,
          // and silently attempt to auto-create a Default template so existing
          // questions (if any) remain accessible immediately.
          this.templates = [];
          this._autoCreateDefaultTemplate();
        } else {
          this.templates = templates;
          const def = templates.find(t => t.is_default) || templates[0];
          this.currentTemplateId = def.id;
          this._loadQuestions();
        }
      },
      error: () => {
        // getTemplates failed (RLS / table not yet migrated).
        // Leave templatesEnabled = true so the Add button stays visible.
        this.templates = [];
        this._loadQuestions();
      }
    });
  }

  private _autoCreateDefaultTemplate(): void {
    this._templateService.createTemplate('Default', true).subscribe({
      next: (result) => {
        const newTemplate: QuestionTemplate = Array.isArray(result) ? result[0] : result;
        if (newTemplate?.id) {
          this.templates = [newTemplate];
          this.currentTemplateId = newTemplate.id;
          this._migrateExistingQuestionsToTemplate(newTemplate.id);
        } else {
          this._loadQuestions();
        }
      },
      error: () => {
        this.templates = [];
        this._loadQuestions();
      }
    });
  }

  private _migrateExistingQuestionsToTemplate(templateId: string): void {
    this._questionService.loadQuestions(undefined).subscribe({
      next: (questions) => {
        const active = questions.filter((q: Question) => q.status);
        if (active.length > 0) {
          const updated = active.map((q: Question) => ({ ...q, template_id: templateId }));
          this._questionService.addOldQustion(updated).subscribe({
            next: () => this._loadQuestions(),
            error: () => this._loadQuestions()
          });
        } else {
          this._loadQuestions();
        }
      },
      error: () => this._loadQuestions()
    });
  }

  private _switchToNewTemplate(id: string): void {
    this.currentTemplateId = id;
    this.isEditMode = true;
    this.isPublished = false;
    this.savedQuestions = [];
    this.questionsToDelete = [];
    this.questions.clear();
    this._loadDefaultQuestions({ autoEditMode: false, markPristine: false });
    this.questionForm.markAsDirty();
  }

  private _loadGeneratedQuestions(generated: { question_text: string }[]): void {
    this.questions.clear();
    const limit = Math.min(generated.length, 15);
    for (let i = 0; i < limit; i++) {
      this.questions.push(this._createQuestionFormGroup({ question_text: generated[i].question_text }));
    }
    this.questionForm.markAsDirty();
  }

  private _loadQuestions(options: { repopulateForm?: boolean } = {}): void {
    const templateId = this.templatesEnabled && this.currentTemplateId
      ? this.currentTemplateId
      : undefined;

    this._questionService.loadQuestions(templateId).subscribe({
      next: (questions) => {
        const activeQuestions = questions.filter((q: Question) => q.status);
        // Sort questions by order
        this.savedQuestions = activeQuestions.sort((a: Question, b: Question) => {
          return (a.order || 0) - (b.order || 0);
        });
        this.isPublished = activeQuestions.length > 0;
        // Only populate form if no saved questions (for default questions)
        if (activeQuestions.length === 0) {
          this._loadDefaultQuestions({ autoEditMode: true, markPristine: true });
          this.questionsToDelete = [];
          this.isPublished = false;
        } else if (options.repopulateForm && this.isEditMode) {
          this._populateFormForEditing();
          this.questionForm.markAsPristine();
        }
      }
    });
  }

  private _populateFormForEditing(): void {
    // Populate form with current saved questions for editing
    this.questions.clear();
    this.savedQuestions.forEach(q => this.questions.push(this._createQuestionFormGroup(q)));
  }

  private _populateFormWithQuestions(questions: Question[]): void {
    // Clear existing questions first to prevent duplication
    this.questions.clear();
    
    const activeQuestions = questions.filter((q: Question) => q.status);
    if (activeQuestions.length) {
      // Sort questions by order
      const sortedQuestions = activeQuestions.sort((a: Question, b: Question) => {
        return (a.order || 0) - (b.order || 0);
      });
      sortedQuestions.forEach(q => this.questions.push(this._createQuestionFormGroup(q)));
    } else {
      this._loadDefaultQuestions();
    }
  }

  private _createQuestionFormGroup(question?: Question): FormGroup {
    const controls: { [key: string]: any } = {
      question_text: [question?.question_text || '', Validators.required]
    };
    if (question?.id) controls['id'] = [question.id];
    if (question?.order !== undefined) controls['order'] = [question.order];
    // Include template_id in the payload when templates are active
    if (this.templatesEnabled && this.currentTemplateId) {
      controls['template_id'] = [question?.template_id ?? this.currentTemplateId];
    }
    return this._fb.group(controls);
  }

  private _loadDefaultQuestions(options: { autoEditMode?: boolean; markPristine?: boolean } = {}): void {
    this.questions.clear();
    DEFAULT_QUESTIONS.forEach(qText => {
      this.questions.push(this._createQuestionFormGroup({ question_text: qText }));
    });

    if (options.autoEditMode) {
      this.isEditMode = true;
      this.isPublished = false;
    }

    if (options.markPristine) {
      this.questionForm.markAsPristine();
      this.questionForm.markAsUntouched();
    }
  }

  private _resetFormAndReload(showNotification?: boolean): void {
    this._setRequestPending(false);
    this.isEditMode = false;
    this.isPublished = false;
    this.questionsToDelete = [];
    this.questions.clear();
    this.questionForm.reset();
    
    // Create default questions for both form and display
    const defaultQuestions = DEFAULT_QUESTIONS.map(qText => ({ 
      question_text: qText, 
      status: true 
    }));
    this.savedQuestions = [...defaultQuestions]; // For view mode display
    
    this._loadDefaultQuestions(); // Load default questions into form
    if (!showNotification) {
      this._notify('Questions reset to default values');
    }
  }

  private _setRequestPending(status: boolean): void {
    this.isRequestPending = status;
  }

  private _trackEvent(eventName: string, data: object): void {
    this._amplitudeService.trackEvent(eventName, data);
  }

  private _notify(message: string): void {
    this._setRequestPending(false);
    this._notificationService.notify(message);
  }

  private _handleError(message: string): void {
    this._notify(message);
  }

  private _handleSuccess(): void {
    this._setRequestPending(false);
    this._notify('Questions saved successfully');
  }

  private _handleSubmitSuccess(): void {
    this._setRequestPending(false);
    this._notify('Questions saved successfully');
    // Exit edit mode and reload questions
    this.isPublished = true;
    this.isEditMode = false;
    this.questionsToDelete = []; // Clear deletion queue
    this.questions.clear();
    this.questionForm.reset();
    this._loadQuestions();
  }

  private _handleNoChanges(): void {
    this._setRequestPending(false);
    this._notify('No changes to submit');
  }

  // Drag and drop functionality
  onQuestionDrop(event: CdkDragDrop<string[]>): void {
    if (this.isEditMode) {
      // Move item in FormArray for edit mode
      moveItemInArray(this.questions.controls, event.previousIndex, event.currentIndex);
      
      // Update the form array values to reflect the new order
      const reorderedQuestions = this.questions.controls.map(control => control.value);
      this.questions.clear();
      reorderedQuestions.forEach(q => this.questions.push(this._createQuestionFormGroup(q)));
    } else {
      // Move item in savedQuestions array for view mode
      moveItemInArray(this.savedQuestions, event.previousIndex, event.currentIndex);
    }
    
    // Update the order values
    this._updateQuestionOrder();
  }

  private _updateQuestionOrder(): void {
    // Update order values based on current positions
    const orderUpdates: { id: number, order: number }[] = [];
    
    this.questions.controls.forEach((control, index) => {
      const questionId = control.get('id')?.value;
      if (questionId) {
        control.patchValue({ order: index + 1 });
        orderUpdates.push({ id: questionId, order: index + 1 });
      }
    });

    // Update savedQuestions order as well
    this.savedQuestions.forEach((question, index) => {
      if (question.id) {
        question.order = index + 1;
      }
    });

    // Send updates to backend if there are saved questions with IDs
    if (orderUpdates.length > 0) {
      // For now, we'll save order changes when the user publishes
      // In future, we can implement real-time order updates
    }
  }

  private _getExistingQuestions(): Question[] {
    return (this.questionForm.value.questions as Question[]).filter(q => !!q.id);
  }

  private _splitQuestions(): { newQuestions: Question[]; oldQuestions: Question[] } {
    const questions = this.questionForm.value.questions as Question[];
    return {
      oldQuestions: questions.filter(q => !!q.id),
      newQuestions: questions.filter(q => !q.id)
    };
  }

  private _queueExistingQuestionsForDeletion(): void {
    const ids = new Set<number>(this.questionsToDelete);

    this.savedQuestions.forEach(question => {
      if (typeof question.id === 'number') {
        ids.add(question.id);
      }
    });

    this.questions.controls.forEach(control => {
      const questionId = control.get('id')?.value;
      if (typeof questionId === 'number') {
        ids.add(questionId);
      }
    });

    this.questionsToDelete = Array.from(ids);
  }

  private _saveDefaultQuestions(): void {
    const templateId = this.templatesEnabled ? this.currentTemplateId : null;
    const defaultQuestions = DEFAULT_QUESTIONS.map((qText, index) => ({
      question_text: qText,
      status: true,
      order: index + 1,
      ...(templateId ? { template_id: templateId } : {})
    }));

    this._questionService.addNewQustion(defaultQuestions).subscribe({
      next: () => {
        this._setRequestPending(false);
        this._notify('Questions reset to default values');
        this.questionsToDelete = [];
        this._loadQuestions({ repopulateForm: true }); // Reload from database
      },
      error: () => {
        // Fallback to UI-only reset if database save fails
        this._resetFormAndReload();
      }
    });
  }
}
